const toDeg = (n) => { return n * 180 / Math.PI; };
const toRad = (n) => { return n * Math.PI / 180; };
const wrap360 = (degrees) => {
  if (0<=degrees && degrees<360) return degrees; // avoid rounding due to arithmetic ops if within range
  return (degrees%360+360) % 360; // sawtooth wave p:360, a:360
}

const sphericalCosinus = (lat1, lon1, lat2, lon2) => {
  const radius = 6371e3; // meters
  const dLon = toRad(lon2 - lon1);
  lat1 = toRad(lat1);
  lat2 = toRad(lat2);
  let distance = Math.acos(Math.sin(lat1) * Math.sin(lat2) +
                 Math.cos(lat1) * Math.cos(lat2) * Math.cos(dLon)) * radius;
  return distance;
}

const calculateDistance = (start, end) => {
  const lat1 = parseFloat(start.lat),
        lon1 = parseFloat(start.long),
        lat2 = parseFloat(end.lat),
        lon2 = parseFloat(end.long);

  return sphericalCosinus(lat1, lon1, lat2, lon2);
}

const createCoord = (coord, bearing, distance) => {
  /** http://www.movable-type.co.uk/scripts/latlong.html
  * φ is latitude, λ is longitude, 
  * θ is the bearing (clockwise from north), 
  * δ is the angular distance d/R; 
  * d being the distance travelled, R the earth’s radius*
  **/
  const radius = 6371e3, // meters
        δ = Number(distance) / radius, // angular distance in radians
        θ = toRad(Number(bearing)),
        φ1 = toRad(coord.lat),
        λ1 = toRad(coord.long);

  const φ2 = Math.asin(Math.sin(φ1)*Math.cos(δ) + 
              Math.cos(φ1)*Math.sin(δ)*Math.cos(θ));

  let λ2 = λ1 + Math.atan2(Math.sin(θ) * Math.sin(δ)*Math.cos(φ1),
              Math.cos(δ)-Math.sin(φ1)*Math.sin(φ2));
  // normalise to -180..+180°
  λ2 = (λ2 + 3 * Math.PI) % (2 * Math.PI) - Math.PI; 

  return {lat: toDeg(φ2), long: toDeg(λ2)};
}

const getBearing = (start, end) => {
  const startLat = toRad(start.lat),
        endLat = toRad(end.lat),
        dLong = toRad(end.long - start.long);

  const x = Math.cos(startLat)* Math.sin(endLat) - Math.sin(startLat)*Math.cos(endLat)*Math.cos(dLong)
  const y = Math.sin(dLong) * Math.cos(endLat);

  const r = wrap360(toDeg(Math.atan2(y, x))); // in degrees

  return r;
}

const getCoordEnRoute = (start, end, distance) => {
  const bearing = getBearing(start, end);
  const newCoord = createCoord(start, bearing, distance); 
  return newCoord;   
}

const intermediatePointTo = (start, end, fraction) => {

  const φ1 = toRad(start.lat), λ1 = toRad(start.long);
  const φ2 = toRad(end.lat), λ2 = toRad(end.long);

  // distance between points
  const Δφ = φ2 - φ1;
  const Δλ = λ2 - λ1;
  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2)
      + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const δ = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  const A = Math.sin((1-fraction)*δ) / Math.sin(δ);
  const B = Math.sin(fraction*δ) / Math.sin(δ);

  const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
  const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
  const z = A * Math.sin(φ1) + B * Math.sin(φ2);

  const φ3 = Math.atan2(z, Math.sqrt(x*x + y*y));
  const λ3 = Math.atan2(y, x);

  const lat = toDeg(φ3);
  const lon = toDeg(λ3);

  return [lat, lon];
}

const getAPointEnRoute = (start, end, distance) => {
  const total_distance = calculateDistance(start, end); // meters
  const bearing = getBearing(start, end);
  const point = createCoord(start, bearing, distance); 
  return {lat:point.lat, long:point.long, bearing, distance, total_distance};
}

const getPointsEnRoute = (start, end, perDistance) => {
  const points = [];
  const total_distance = calculateDistance(start, end); // meters
  const bearing = getBearing(start, end);
  let aircraftBearing = bearing;
  let previousStart = start;
  const pointCount = Math.floor(total_distance / perDistance);
  for (let a = 0; a < pointCount; a++) {
    let distance = perDistance * (a + 1);
    const newStart = createCoord(start, bearing, distance); 
    if (a === pointCount - 1) {
      points.push({...end, bearing});  
    } else {
      if (a > 0) {
        aircraftBearing = getBearing(previousStart, newStart);
        previousStart = newStart;
      }
      points.push({...newStart, bearing: aircraftBearing});  
    }
  }
  return points;
}

export {
  getCoordEnRoute,
  getPointsEnRoute,
  getAPointEnRoute,
  intermediatePointTo,
  calculateDistance,
  getBearing
}